import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Upload, FileText, Loader2, CheckCircle, Download, RefreshCw, Eye,
  FileSpreadsheet, Clock, Hash, Target, ChevronDown, ChevronUp,
  Percent, Building2, Trash2, Plane, Ship, SlidersHorizontal,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';

const COUNTRIES = ['China','Russia','Belarus','North Korea','Iran','Syria','Myanmar','Cuba','Venezuela','Libya','Somalia','Sudan','Yemen','Afghanistan','Pakistan','Iraq','Lebanon'];
const COMPLIANCES = ['EU-RoHS','CN-RoHS','REACH','PFAS'];
const QUALIFICATIONS = ['AEC','AUTO-QMS','AUTO-SAFETY','EMC','IEC-COMP','JEDEC','SAFETY-CERT','MIL','SPEC'];
const PRICE_TARGETS = [
  { value: 'best', label: 'Best Rate' },
  { value: 'anchor', label: 'Probabilistic Rate' },
  { value: 'median', label: 'Market Median' },
];

const PillSelect = ({ items, selected, onToggle, colorOn, colorOff, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(v => (
      <button key={v} onClick={() => onToggle(v)}
        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${selected.includes(v) ? colorOn : colorOff}`}
        data-testid={`${testPrefix}-${v}`}>
        {v} {selected.includes(v) ? '\u2713' : ''}
      </button>
    ))}
  </div>
);

const ConstraintsForm = ({ params, setParams, isRerun = false }) => {
  const toggle = (field, val) => setParams(p => ({
    ...p, [field]: p[field].includes(val) ? p[field].filter(x => x !== val) : [...p[field], val],
  }));
  const shipmentDisabled = params.target_incoterm === 'EXW';

  return (
    <div className="space-y-4">
      {/* Row 1: Price Target & Production Quantity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Price Target</Label>
          <Select value={params.price_target} onValueChange={v => setParams(p => ({...p, price_target: v}))}>
            <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-price-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRICE_TARGETS.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">Production Quantity</Label>
          <Input type="number" value={params.production_quantity} onChange={e => setParams(p => ({...p, production_quantity: e.target.value}))}
            placeholder="e.g. 5000" min="1" className="h-8 mt-1 text-sm" data-testid="opt-production-qty" />
        </div>
      </div>

      {/* Row 2: Data Recency & Distributor Type */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Pricing Data Recency</Label>
          <Select value={params.data_recency} onValueChange={v => setParams(p => ({...p, data_recency: v}))}>
            <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-data-recency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['6months','12months','18months','24months','30months','36months'].map(v => (
                <SelectItem key={v} value={v}>{v.replace('months',' Months')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">Distributor Type</Label>
          <Select value={params.distributor_type} onValueChange={v => setParams(p => ({...p, distributor_type: v}))}>
            <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-distributor-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="authorized">Authorized Only</SelectItem>
              <SelectItem value="independent">Independent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Target Incoterm & Shipment Mode */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Target Incoterm</Label>
          <Select value={params.target_incoterm} onValueChange={v => setParams(p => ({...p, target_incoterm: v, shipment_mode: v === 'EXW' ? 'air' : p.shipment_mode}))}>
            <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-incoterm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['EXW','CIF','CIF+DUTY','DDP','BOM Specific'].map(v => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={shipmentDisabled ? 'opacity-50' : ''}>
          <Label className="text-xs font-medium flex items-center gap-1">
            International Shipment Mode
            {shipmentDisabled && <span className="text-[10px] text-gray-400 font-normal">(N/A for EXW)</span>}
          </Label>
          <Select value={params.shipment_mode} onValueChange={v => setParams(p => ({...p, shipment_mode: v}))} disabled={shipmentDisabled}>
            <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-shipment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="air"><span className="flex items-center gap-1"><Plane className="h-3 w-3" /> Air</span></SelectItem>
              <SelectItem value="sea"><span className="flex items-center gap-1"><Ship className="h-3 w-3" /> Sea</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Exclude Country of Supply */}
      <div>
        <Label className="text-xs font-medium">Exclude Country of Supply</Label>
        <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
          {COUNTRIES.map(c => (
            <button key={c} onClick={() => toggle('exclude_countries', c)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${params.exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              data-testid={`opt-country-${c}`}>
              {c} {params.exclude_countries.includes(c) ? '\u2715' : ''}
            </button>
          ))}
        </div>
        {params.exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{params.exclude_countries.length} excluded</p>}
      </div>

      {/* Toggles: Include Alternates, Spot Prices, Imports as Domestic */}
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2">
          <Switch checked={params.include_alternates} onCheckedChange={v => setParams(p => ({...p, include_alternates: v, alternates_exclude_countries: v ? p.alternates_exclude_countries : []}))} data-testid="opt-include-alts" />
          <Label className="text-xs">Include Alternates</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={params.include_spot_prices} onCheckedChange={v => setParams(p => ({...p, include_spot_prices: v}))} data-testid="opt-include-spot" />
          <Label className="text-xs">Include Spot Prices</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={params.imports_as_domestic} onCheckedChange={v => setParams(p => ({...p, imports_as_domestic: v}))} data-testid="opt-imports-domestic" />
          <Label className="text-xs">Imports as Domestic</Label>
        </div>
      </div>

      {/* Alternates: Exclude Country of Origin (shown when include_alternates is on) */}
      {params.include_alternates && (
        <div>
          <Label className="text-xs font-medium">Alternates: Exclude Country of Origin</Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
            {COUNTRIES.map(c => (
              <button key={`alt-${c}`} onClick={() => toggle('alternates_exclude_countries', c)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${params.alternates_exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                data-testid={`opt-alt-country-${c}`}>
                {c} {params.alternates_exclude_countries.includes(c) ? '\u2715' : ''}
              </button>
            ))}
          </div>
          {params.alternates_exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{params.alternates_exclude_countries.length} excluded</p>}
        </div>
      )}

      {/* Compliances */}
      <div>
        <Label className="text-xs font-medium">Compliances</Label>
        <div className="mt-1.5">
          <PillSelect items={COMPLIANCES} selected={params.compliances}
            onToggle={v => toggle('compliances', v)}
            colorOn="bg-green-100 text-green-800 border-green-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
            testPrefix="opt-compliance" />
        </div>
      </div>

      {/* Qualifications */}
      <div>
        <Label className="text-xs font-medium">Qualifications</Label>
        <div className="mt-1.5">
          <PillSelect items={QUALIFICATIONS} selected={params.qualifications}
            onToggle={v => toggle('qualifications', v)}
            colorOn="bg-purple-100 text-purple-800 border-purple-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
            testPrefix="opt-qual" />
        </div>
      </div>
    </div>
  );
};

const defaultConstraints = () => ({
  price_target: 'anchor',
  production_quantity: '',
  data_recency: '12months',
  distributor_type: 'all',
  target_incoterm: 'EXW',
  shipment_mode: 'air',
  exclude_countries: [],
  include_alternates: true,
  include_spot_prices: true,
  imports_as_domestic: false,
  alternates_exclude_countries: [],
  compliances: [],
  qualifications: [],
});

const EMSSimulator = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [marginPercent, setMarginPercent] = useState('15');
  const [uploading, setUploading] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);
  const [constraints, setConstraints] = useState(defaultConstraints());

  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunSim, setRerunSim] = useState(null);
  const [rerunMargin, setRerunMargin] = useState('15');
  const [rerunConstraints, setRerunConstraints] = useState(defaultConstraints());
  const [rerunning, setRerunning] = useState(false);

  const fetchSimulations = useCallback(async () => {
    try {
      const res = await api.get('/simulator/list/all');
      setSimulations(res.data.simulations || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSimulations();
    const interval = setInterval(fetchSimulations, 5000);
    return () => clearInterval(interval);
  }, [fetchSimulations]);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      if (!projectName) setProjectName(f.name.replace(/\.(csv|xlsx|xls)$/i, '').substring(0, 30));
      toast.success(`Selected: ${f.name}`);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const serializeConstraints = (c) => {
    const fd = {};
    fd.price_target = c.price_target || 'anchor';
    fd.production_quantity = c.production_quantity || '';
    fd.data_recency = c.data_recency || '12months';
    fd.distributor_type = c.distributor_type || 'all';
    fd.target_incoterm = c.target_incoterm || 'EXW';
    fd.shipment_mode = c.shipment_mode || 'air';
    fd.exclude_countries = (c.exclude_countries || []).join(',');
    fd.include_alternates = c.include_alternates ? 'true' : 'false';
    fd.include_spot_prices = c.include_spot_prices ? 'true' : 'false';
    fd.imports_as_domestic = c.imports_as_domestic ? 'true' : 'false';
    fd.alternates_exclude_countries = (c.alternates_exclude_countries || []).join(',');
    fd.compliances = (c.compliances || []).join(',');
    fd.qualifications = (c.qualifications || []).join(',');
    return fd;
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!projectName.trim()) { toast.error('Please enter a Project Name'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_name', projectName);
      formData.append('company_name', companyName);
      formData.append('margin_percent', marginPercent);
      const serialized = serializeConstraints(constraints);
      Object.entries(serialized).forEach(([k, v]) => formData.append(k, v));

      const res = await api.post('/simulator/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(res.data.message);
      setFile(null);
      setProjectName('');
      fetchSimulations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const openRerun = (sim) => {
    setRerunSim(sim);
    setRerunMargin(String(sim.margin_percent || 15));
    const c = sim.constraints || {};
    setRerunConstraints({
      price_target: c.price_target || 'anchor',
      production_quantity: c.production_quantity || '',
      data_recency: c.data_recency || '12months',
      distributor_type: c.distributor_type || 'all',
      target_incoterm: c.target_incoterm || 'EXW',
      shipment_mode: c.shipment_mode || 'air',
      exclude_countries: c.exclude_countries || [],
      include_alternates: c.include_alternates !== false,
      include_spot_prices: c.include_spot_prices !== false,
      imports_as_domestic: c.imports_as_domestic === true,
      alternates_exclude_countries: c.alternates_exclude_countries || [],
      compliances: c.compliances || [],
      qualifications: c.qualifications || [],
    });
    setRerunOpen(true);
  };

  const handleRerun = async () => {
    if (!rerunSim) return;
    setRerunning(true);
    try {
      const formData = new FormData();
      formData.append('margin_percent', rerunMargin);
      const serialized = serializeConstraints(rerunConstraints);
      Object.entries(serialized).forEach(([k, v]) => formData.append(k, v));
      await api.post(`/simulator/${rerunSim.sim_id}/rerun`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Re-simulating with updated parameters');
      setRerunOpen(false);
      fetchSimulations();
    } catch { toast.error('Rerun failed'); }
    setRerunning(false);
  };

  const deleteSim = async (simId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/simulator/${simId}`);
      setSimulations(prev => prev.filter(s => s.sim_id !== simId));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const constraintCount = constraints.exclude_countries.length + constraints.compliances.length +
    constraints.qualifications.length + constraints.alternates_exclude_countries.length +
    (constraints.distributor_type !== 'all' ? 1 : 0) + (constraints.target_incoterm !== 'EXW' ? 1 : 0) +
    (constraints.shipment_mode !== 'air' ? 1 : 0) + (constraints.data_recency !== '12months' ? 1 : 0) +
    (constraints.price_target !== 'anchor' ? 1 : 0) + (!constraints.include_alternates ? 1 : 0) +
    (!constraints.include_spot_prices ? 1 : 0) + (constraints.imports_as_domestic ? 1 : 0);

  return (
    <Layout title="Quote Builder" subtitle="Upload a BOM, set pricing constraints & margin, generate a customer-facing quote">
      <div className="max-w-5xl mx-auto space-y-6" data-testid="simulator-page">

        <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <h2 className="text-white font-semibold text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" /> New Quote
            </h2>
            <p className="text-emerald-100 text-sm mt-0.5">Upload a BOM, set pricing constraints & margin, and generate a customer-facing quote.</p>
          </div>
          <CardContent className="p-6 space-y-4">

            {/* Project Name */}
            <div>
              <Label className="text-xs font-semibold text-gray-800">Project Name <span className="text-red-500">*</span></Label>
              <Input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="e.g. OEM-X Smart Thermostat RFQ"
                className="mt-1 h-10 text-sm font-medium" data-testid="input-project-name" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-700">Your Company Name (branding)</Label>
                <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Acme Electronics Ltd."
                  className="mt-1 h-9 text-sm" data-testid="input-company-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-700 flex items-center gap-1"><Percent className="h-3 w-3" /> Blanket Margin %</Label>
                <Input type="number" value={marginPercent} onChange={e => setMarginPercent(e.target.value)} placeholder="15"
                  min="0" max="100" step="0.5" className="mt-1 h-9 text-sm" data-testid="input-margin" />
              </div>
            </div>

            <div className="flex items-start gap-3 text-sm bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <FileSpreadsheet className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-700">Expected CSV/XLSX columns</p>
                <p className="text-gray-500 mt-0.5">
                  <span className="font-medium text-gray-700">MPN</span>, <span className="font-medium text-gray-700">CPN</span> (either one is required), <span className="font-medium text-gray-700">Per Board Qty</span>, <span className="text-gray-400">Make</span> (optional)
                </p>
              </div>
            </div>

            {/* File selection */}
            <div>
              {file ? (
                <div className="flex items-center gap-3 px-3 py-2 bg-emerald-50/70 border border-emerald-200 rounded-lg" data-testid="selected-file-chip">
                  <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="text-sm text-emerald-700 flex-1 truncate">{file.name}</span>
                  <button className="text-emerald-400 hover:text-red-500" onClick={() => setFile(null)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileSelect} data-testid="bom-file-input" />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Drop BOM file or click to browse</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">CSV format (.csv)</p>
                  </div>
                </label>
              )}
            </div>

            {/* Pricing Constraints */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="pricing-constraints">
              <button className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setShowCriteria(!showCriteria)} data-testid="toggle-criteria">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-gray-500" /> Pricing Constraints
                </span>
                <div className="flex items-center gap-2">
                  {constraintCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{constraintCount} modified</Badge>}
                  {showCriteria ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </button>
              {showCriteria && (
                <div className="p-4 border-t border-gray-200">
                  <ConstraintsForm params={constraints} setParams={setConstraints} />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" className="text-xs text-gray-500 gap-1"
                onClick={() => { const a = document.createElement('a'); a.href = '/samples/Sample_Quote.csv'; a.download = 'Quote_template.csv'; a.click(); }}
                data-testid="btn-download-template">
                <Download className="h-3.5 w-3.5" /> Download Template (.csv)
              </Button>
              <Button onClick={handleUpload} disabled={!file || uploading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="generate-quote-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : <><Target className="h-4 w-4" /> Generate Quote</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Projects Listing */}
        <div data-testid="recent-simulations-section">
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" /> Projects
          </h3>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
                </div>
              ) : simulations.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No projects yet. Upload a BOM above to generate your first quote.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-xs">
                        <TableHead className="font-semibold">Project Name</TableHead>
                        <TableHead className="font-semibold">Company</TableHead>
                        <TableHead className="font-semibold text-center">Margin</TableHead>
                        <TableHead className="font-semibold text-center"><Hash className="h-3 w-3 inline mr-0.5" />Lines</TableHead>
                        <TableHead className="font-semibold text-right">Quoted Total</TableHead>
                        <TableHead className="font-semibold text-center">Status</TableHead>
                        <TableHead className="font-semibold text-center">Quote</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {simulations.map((s) => {
                        const isProcessing = s.status === 'processing';
                        const isCompleted = s.status === 'completed';
                        return (
                          <TableRow key={s.sim_id} className="hover:bg-gray-50 text-xs" data-testid={`sim-row-${s.sim_id}`}>
                            <TableCell className="font-medium text-gray-800 max-w-[180px] truncate" title={s.project_name}>
                              <FileSpreadsheet className="h-3.5 w-3.5 inline mr-1.5 text-gray-400" />
                              {s.project_name || 'Untitled'}
                            </TableCell>
                            <TableCell className="text-gray-500 max-w-[120px] truncate">
                              {s.ems_branding?.company_name ? (
                                <span className="flex items-center gap-1"><Building2 className="h-3 w-3 text-gray-400" /> {s.ems_branding.company_name}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-[10px]">{s.margin_percent || 0}%</Badge>
                            </TableCell>
                            <TableCell className="text-center font-medium">{s.total_lines ?? '-'}</TableCell>
                            <TableCell className="text-right font-semibold text-emerald-700">
                              {isCompleted && s.summary?.total_quoted_cost ? `$${s.summary.total_quoted_cost.toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              {isProcessing ? (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Processing</Badge>
                              ) : isCompleted ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px] gap-1"><CheckCircle className="h-2.5 w-2.5" /> Completed</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">{s.status}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {(() => {
                                const qs = s.quote_status || 'draft';
                                const colors = { draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-700', accepted: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700', revised: 'bg-amber-100 text-amber-700' };
                                return <Badge className={`text-[10px] ${colors[qs] || colors.draft}`}>{qs.charAt(0).toUpperCase() + qs.slice(1)}</Badge>;
                              })()}
                            </TableCell>
                            <TableCell className="text-gray-500">{formatDate(s.created_at)}</TableCell>
                            <TableCell className="text-right space-x-1">
                              {isCompleted && (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => navigate(`/project-simulator/${s.sim_id}`)} data-testid={`btn-view-${s.sim_id}`}>
                                    <Eye className="h-3 w-3" /> View
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => openRerun(s)} data-testid={`btn-rerun-${s.sim_id}`}>
                                    <RefreshCw className="h-3 w-3" /> Re-Simulate
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                                onClick={(e) => deleteSim(s.sim_id, e)} data-testid={`btn-delete-${s.sim_id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Re-Simulate Dialog */}
        <Dialog open={rerunOpen} onOpenChange={setRerunOpen}>
          <DialogContent className="max-w-lg max-h-[85vh]" data-testid="rerun-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
                Re-Simulate Quote
              </DialogTitle>
              <DialogDescription className="text-xs">
                Update parameters for <strong>{rerunSim?.project_name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-xs font-medium">Blanket Margin %</Label>
                <Input type="number" value={rerunMargin} onChange={e => setRerunMargin(e.target.value)}
                  min="0" max="100" step="0.5" className="mt-1 h-8 text-sm" data-testid="rerun-margin" />
              </div>
              <ConstraintsForm params={rerunConstraints} setParams={setRerunConstraints} isRerun />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRerunOpen(false)}>Cancel</Button>
              <Button onClick={handleRerun} disabled={rerunning}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirm-rerun">
                {rerunning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Re-Simulate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EMSSimulator;
